/**
 * Ad Studio → Audio Studio (`/ad-studio/audio`, gated like the rest of Ad Studio by `show_cowork`).
 * A banner in the style of Omni's Home welcome card (dark purple gradient, green eyebrow, "Audio Studio" with the accent
 * word, Beta, one subtitle) holds the three tabs as pills — and, on the Studio tab, the prompt bar at its bottom:
 *   - Studio (default): PromptBar in the banner, then two columns (≥ 1024px: `minmax(0,1fr) 360px`, one 24px gutter)
 *     sharing three rows so they line up — titles + notes, toolbars, then the trend cards next to the "Recent
 *     creations" panel. The panel is sticky and scrolls on its own: by default it lists Recent creations (generations in
 *     progress / failed, then the latest 8 sounds; "See all" → Your Collections › All sounds); opening a trend card
 *     switches it to that song (TrendStage: song, rights, "Remix", TikTok player, Details → drawer) until ← Back.
 *     Below 1024px Recent creations stacks under the trends and a card opens the song drawer instead.
 *     Every "Remix" (card, stage, drawer) opens RemixDialog; an accepted remix lands at the top of Recent creations —
 *     the stage makes way for the list on desktop, and below 1024px the page scrolls down to it.
 *   - Your Collections: a Spotify-like home (brand filter, Recent creations shelf, collection cards with "All sounds"
 *     first) and a playlist page per collection (`&collection=<id>`, `&collection=all` for All sounds).
 *   - Library: Beatly's curated catalogue (playlists, search, filters).
 * One AudioPlayerProvider (one <audio>) is shared by every tab, with a docked mini player; the page keeps the
 * player's height clear at the bottom so nothing ends up hidden behind it.
 * Brands: the banner's "For <brand> ▾" chip (Studio and Your Collections) sets the app-wide active brand; new sounds
 * and remixes are made for it and Your Collections filters by it by default (StudioBrandsProvider).
 *
 * Deep links: `?section=studio|collections|library` (+ `&collection=<id>` / `all`). Older links still work:
 * `create` / `trends` / `studio` → Studio, `mine` / `yours` → Your Collections › All sounds, `library` / `open` →
 * Library (with `&view=yours`, the old Library › Yours, → All sounds); `?tab=` is read like `?section=`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/components/ui/utils';
import { AudioPlayerProvider, MiniPlayer, usePlayerInset } from './AudioPlayer';
import { BrandChip, StudioBrandsProvider } from './brands';
import { ALL_SOUNDS } from './collectionKeys';
import { CollectionsTab } from './CollectionsTab';
import { LibrarySection } from './LibrarySection';
import { PromptBar } from './PromptBar';
import { RemixDialog } from './RemixDialog';
import type { SoundsScope } from './SoundRows';
import { TrendSongDrawer } from './TrendSongDrawer';
import { TrendStage } from './TrendStage';
import { TrendsSection } from './TrendsSection';
import { YourSoundsRail } from './YourSoundsRail';
import { useStudioJobs, type StudioJobRef } from './studioJobs';
import type { TrendFocus } from './trendInsights';
import { DESKTOP_QUERY, useMediaQuery } from './useMediaQuery';

type Tab = 'studio' | 'collections' | 'library';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'studio', label: 'Studio' },
  { id: 'collections', label: 'Your Collections' },
  { id: 'library', label: 'Library' },
];

interface Arrival {
  tab: Tab;
  /** The collection to open in Your Collections (null = the home, `all` = All sounds). */
  collection: string | null;
  /** Where to take the user inside the Studio tab. */
  focus: 'prompt' | 'trends' | null;
}

/** Reads `?section=` (or the older `?tab=`) and maps every value we ever shipped onto the three tabs. */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper, unit-tested with the page
export function parseArrival(params: URLSearchParams): Arrival {
  const v = params.get('section') || params.get('tab') || '';
  const view = params.get('view');
  const none = { collection: null, focus: null };
  if (v === 'collections') return { ...none, tab: 'collections', collection: params.get('collection') || null };
  // "Your sounds" links (and the old Library › Yours view) now land on Your Collections › All sounds.
  if (v === 'mine' || v === 'yours') return { ...none, tab: 'collections', collection: ALL_SOUNDS };
  if (v === 'library' || v === 'open') return view === 'yours' ? { ...none, tab: 'collections', collection: ALL_SOUNDS } : { ...none, tab: 'library' };
  if (v === 'create') return { ...none, tab: 'studio', focus: 'prompt' };
  if (v === 'trends') return { ...none, tab: 'studio', focus: 'trends' };
  return { ...none, tab: 'studio' };
}

/** jsdom and very old browsers lack scrollIntoView — never let a scroll nicety throw. */
function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Tab pills for the dark banner: the active one filled Beatly neon green, the others white text. */
const pillCls = (on: boolean) => cn(
  'h-8 shrink-0 whitespace-nowrap rounded-full px-3.5 text-[14px] leading-5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#a9f600]/60',
  on ? 'bg-[#a9f600] text-[#2e024c]' : 'text-white/85 hover:bg-white/10 hover:text-white',
);

const SUBTITLE = 'Find, remix and create music for your creators’ ads — every sound is cleared for brand use.';

export function AudioStudioPage() {
  return (
    <StudioBrandsProvider>
      <AudioPlayerProvider>
        <AudioStudioScreen />
        <MiniPlayer />
      </AudioPlayerProvider>
    </StudioBrandsProvider>
  );
}

function AudioStudioScreen() {
  const [params, setParams] = useSearchParams();
  // The deep link is read once, on arrival.
  const [arrival] = useState<Arrival>(() => parseArrival(params));
  // `?playlist=` (old adstudios.beatly.com playlist links) opens that playlist in the Library.
  const [arrivalPlaylist] = useState(() => params.get('playlist'));
  const [tab, setTabState] = useState<Tab>(arrival.tab);
  /** The open collection in Your Collections (null = the home, `all` = All sounds). */
  const [collection, setCollection] = useState<string | null>(arrival.collection);
  const [allScope, setAllScope] = useState<SoundsScope>('team');
  // Your Collections and the Library only mount once visited; the Studio stays mounted so a typed prompt and running
  // jobs survive tab switches.
  const [visited, setVisited] = useState<ReadonlySet<Tab>>(() => new Set([arrival.tab]));
  /** The trend on the right-hand stage (desktop); null = the stage shows Recent creations. */
  const [staged, setStaged] = useState<TrendFocus | null>(null);
  /** The trend in the details drawer (smaller screens, or "Details" on the stage). */
  const [drawer, setDrawer] = useState<TrendFocus | null>(null);
  /** The trend in the Remix dialog; null = closed. */
  const [remix, setRemix] = useState<TrendFocus | null>(null);
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const { jobs, addJob, dismissJob } = useStudioJobs();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const inset = usePlayerInset();

  useEffect(() => {
    if (arrival.focus === 'trends') scrollToId('audio-trends');
    else if (arrival.focus === 'prompt') inputRef.current?.focus();
  }, [arrival.focus]);

  /**
   * Switch tab (and, for Your Collections, optionally the open collection) and keep the URL shareable
   * (`?section=…&collection=…`), dropping the legacy `tab` / `view` params.
   */
  const go = useCallback((next: Tab, opts: { collection?: string | null } = {}) => {
    const nextCollection = opts.collection !== undefined ? opts.collection : collection;
    setTabState(next);
    setVisited((v) => (v.has(next) ? v : new Set([...v, next])));
    if (opts.collection !== undefined) setCollection(opts.collection);
    const q = new URLSearchParams(params);
    q.delete('tab');
    q.delete('view');
    q.set('section', next);
    if (next === 'collections' && nextCollection) q.set('collection', nextCollection);
    else q.delete('collection');
    setParams(q, { replace: true });
  }, [params, setParams, collection]);

  /** A card (body or ›): the stage on desktop, the drawer below 1024px. */
  const openTrend = useCallback((focus: TrendFocus) => {
    if (isDesktop) setStaged(focus);
    else setDrawer(focus);
  }, [isDesktop]);

  /** A new generation goes back to Recent creations so its progress is in view. */
  const jobStarted = useCallback((job: StudioJobRef) => {
    addJob(job);
    setStaged(null);
  }, [addJob]);

  /** A remix was accepted: on desktop the stage already gave way to the list; below 1024px jump down to it. */
  const remixStarted = useCallback((job: StudioJobRef) => {
    jobStarted(job);
    if (!isDesktop) scrollToId('audio-recent');
  }, [jobStarted, isDesktop]);

  const stage = isDesktop ? staged : null;

  /** Recent creations → See all: Your Collections › All sounds, with the same Team / Mine. */
  const seeAllSounds = useCallback((scope: SoundsScope) => {
    setAllScope(scope);
    go('collections', { collection: ALL_SOUNDS });
    scrollToId('audio-studio-tabs');
  }, [go]);

  /** "Make a sound" from an empty Your Collections: the Studio's prompt. */
  const createSound = useCallback(() => {
    go('studio');
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [go]);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 pt-5 sm:px-6 lg:px-7 lg:pt-7" style={{ paddingBottom: 40 + inset }}>
      {/* The banner — Home's welcome-card style: eyebrow, title with the accent word, subtitle, the tabs and, on the
          Studio tab, the prompt bar along its bottom. */}
      <header className="relative overflow-hidden rounded-[24px] bg-gradient-to-r from-[#2e024c] to-[#5a1a8a] shadow-lg">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #be43ff 0%, transparent 50%), radial-gradient(circle at 20% 80%, #a9f600 0%, transparent 50%)' }}
        />
        <div className="relative z-10 p-5 sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0">
              <p className="mb-2 text-sm text-[#a9f600]">Ad Studio</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="text-[24px] leading-tight text-white sm:text-[28px] lg:text-[36px]" style={{ fontWeight: 700 }}>
                  Audio <span className="text-[#be43ff]">Studio</span>
                </h1>
                {/* LABS: pilot surface — gated by show_cowork (routes.ts COWORK_GATED_PLATFORM_PAGES). */}
                <span className="inline-flex h-6 items-center rounded-full bg-white/15 px-2 text-[11.5px] font-semibold leading-4 text-white">Beta</span>
                {tab !== 'library' && <BrandChip />}
              </div>
              <p className="mt-2 max-w-lg text-sm text-white/60">{SUBTITLE}</p>
            </div>

            {/* Scrolls sideways on narrow phones rather than wrapping. */}
            <div
              id="audio-studio-tabs"
              role="tablist"
              aria-label="Audio Studio"
              className="-mx-5 flex scroll-mt-4 items-center gap-1 overflow-x-auto px-5 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
            >
              {TABS.map((t) => (
                <button key={t.id} id={`audio-tab-${t.id}`} type="button" role="tab" aria-selected={tab === t.id} aria-controls={`audio-panel-${t.id}`} onClick={() => go(t.id)} className={pillCls(tab === t.id)} style={{ fontWeight: 600 }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Kept mounted (hidden) off the Studio tab so a typed prompt survives tab switches. */}
          <div data-banner-part="prompt" className="mt-6" hidden={tab !== 'studio'}>
            <PromptBar onJobStarted={jobStarted} inputRef={inputRef} />
            {jobs.length > 0 && (
              <button type="button" onClick={() => scrollToId('audio-recent')} className="mt-2 text-[12.5px] text-white/80 underline-offset-2 hover:underline lg:hidden">
                {jobs.length} generation{jobs.length === 1 ? '' : 's'} — see Recent creations ↓
              </button>
            )}
          </div>
        </div>
      </header>

      <div id="audio-panel-studio" role="tabpanel" aria-labelledby="audio-tab-studio" hidden={tab !== 'studio'}>
        {/* Two columns sharing three rows (each section is a subgrid): titles, toolbars, then cards | panel. */}
        <div className="mt-8 grid grid-cols-1 gap-x-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:grid-rows-[auto_auto_1fr]">
          <TrendsSection selectedId={stage?.trend.trend_id ?? null} onRemix={setRemix} onOpen={openTrend} />
          {/* The list stays mounted under a staged trend so running generations keep their hand-over state. */}
          <YourSoundsRail
            jobs={jobs}
            onDismissJob={dismissJob}
            onJobStarted={jobStarted}
            onSeeAll={seeAllSounds}
            onScopeChange={() => setStaged(null)}
            stage={stage && (
              <TrendStage
                key={stage.trend.trend_id}
                focus={stage}
                onBack={() => setStaged(null)}
                onRemix={() => setRemix(stage)}
                onDetails={() => setDrawer(stage)}
              />
            )}
          />
        </div>

        <TrendSongDrawer
          trend={drawer?.trend ?? null}
          open={Boolean(drawer)}
          onOpenChange={(o) => { if (!o) setDrawer(null); }}
          onLookalike={() => { if (drawer) { const d = drawer; setDrawer(null); setRemix(d); } }}
          region={drawer?.region}
          placeholderCover={drawer?.placeholderCover}
        />

        <RemixDialog focus={remix} onOpenChange={(o) => { if (!o) setRemix(null); }} onStarted={remixStarted} />
      </div>

      <div id="audio-panel-collections" role="tabpanel" aria-labelledby="audio-tab-collections" hidden={tab !== 'collections'} className="mt-8">
        {visited.has('collections') && (
          <CollectionsTab selected={collection} onSelect={(id) => go('collections', { collection: id })} allScope={allScope} onCreateSound={createSound} />
        )}
      </div>

      <div id="audio-panel-library" role="tabpanel" aria-labelledby="audio-tab-library" hidden={tab !== 'library'} className="mt-8">
        {visited.has('library') && <LibrarySection initialPlaylistId={arrivalPlaylist} />}
      </div>
    </div>
  );
}
